/**
 * AufgabenBaum — Aufgaben mit Unteraufgaben als aufklappbarer Baum (Strato-Stil).
 *
 * Wird überall gleich verwendet: Aufgabenseite (mittlere Spalte), Adressen, Projekte
 * und im Aufgabenfenster für die Unteraufgaben.
 *
 * Props:
 *   aufgaben          flache Liste mit parent_task_id (Unteraufgaben beliebig tief)
 *   ausgewaehltId     markierte Aufgabe (oranger Balken)
 *   onAuswahl(task)   Klick auf die Zeile
 *   onErledigt(task, erledigt)  Häkchen
 *   onUnteraufgabe?(task)       „+“ an der Zeile: Unteraufgabe anlegen
 *   onKontextmenue?(e, task)
 *   erledigteGetrennt Abgeschlossene Hauptaufgaben unten in eigenem Bereich (Standard: ja)
 *   speicherSchluessel  merkt zugeklappte Knoten im Browser
 *   kompakt           ohne Prioritätsanzeige (z. B. im Aufgabenfenster)
 *   leerText          Text, wenn keine offenen Aufgaben vorhanden sind
 */
import { useCallback, useMemo, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { StatusEtikett, Fortschrittsbalken } from '../detailfenster/DetailFenster';
import {
  baueBaum, deDatum, fortschritt, istErledigt, istUeberfaellig, prioritaetInfo, statusInfo, unteraufgabenZaehler,
} from './aufgabenKonstanten';

function useZugeklappt(speicherSchluessel) {
  const [zu, setZu] = useState(() => {
    if (!speicherSchluessel) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(speicherSchluessel) || '[]')); } catch { return new Set(); }
  });
  const umschalten = useCallback((id) => {
    setZu((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      if (speicherSchluessel) {
        try { localStorage.setItem(speicherSchluessel, JSON.stringify([...neu])); } catch { /* ignorieren */ }
      }
      return neu;
    });
  }, [speicherSchluessel]);
  const alleSetzen = useCallback((ids) => {
    const neu = new Set(ids);
    setZu(neu);
    if (speicherSchluessel) {
      try { localStorage.setItem(speicherSchluessel, JSON.stringify([...neu])); } catch { /* ignorieren */ }
    }
  }, [speicherSchluessel]);
  return [zu, umschalten, alleSetzen];
}

function Zeile({ k, ebene, zu, umschalten, props }) {
  const { ausgewaehltId, onAuswahl, onErledigt, onUnteraufgabe, onKontextmenue, kompakt } = props;
  const erledigt = istErledigt(k);
  const hatKinder = k.kinder.length > 0;
  const offen = hatKinder && !zu.has(k.id);
  const zaehler = unteraufgabenZaehler(k);
  const ueberfaellig = istUeberfaellig(k);
  const prio = prioritaetInfo(k.priority);
  const st = statusInfo(k.status);
  const gewaehlt = k.id === ausgewaehltId;

  return (
    <div data-testid={`aufgaben-baum-knoten-${k.id}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onAuswahl?.(k)}
        onKeyDown={(e) => { if (e.key === 'Enter') onAuswahl?.(k); }}
        onContextMenu={onKontextmenue ? (e) => onKontextmenue(e, k) : undefined}
        className={`group grid grid-cols-[18px_18px_minmax(0,1fr)_auto] gap-x-2 items-center px-2.5 py-1.5 border-l-[3px] rounded-r cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-[#2B2FB5] ${gewaehlt ? 'bg-[#ececf3] border-l-[#E67E22]' : 'border-l-transparent hover:bg-gray-50'}`}
        data-testid={`aufgaben-baum-zeile-${k.id}`}
      >
        {hatKinder ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); umschalten(k.id); }}
            aria-label={offen ? 'Unteraufgaben zuklappen' : 'Unteraufgaben aufklappen'}
            className="w-[18px] h-[18px] grid place-items-center rounded text-gray-500 hover:bg-gray-200"
            data-testid={`aufgaben-baum-toggle-${k.id}`}
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${offen ? '' : '-rotate-90'}`} />
          </button>
        ) : <span />}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onErledigt?.(k, !erledigt); }}
          aria-label={erledigt ? 'Wieder öffnen' : 'Als erledigt markieren'}
          className={`w-4 h-4 rounded-[3px] border-[1.5px] grid place-items-center ${erledigt ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white' : 'border-gray-400 bg-white hover:border-[#1E3A5F]'}`}
          data-testid={`aufgaben-baum-check-${k.id}`}
        >
          {erledigt && <Check className="w-3 h-3" strokeWidth={3.5} />}
        </button>
        <span className={`text-sm font-medium truncate ${erledigt ? 'line-through text-gray-400' : 'text-[#1E3A5F]'}`} title={k.title}>
          {k.title}
        </span>
        <span className="flex items-center gap-1.5">
          {onUnteraufgabe && !erledigt && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onUnteraufgabe(k); }}
              title="Unteraufgabe hinzufügen"
              aria-label="Unteraufgabe hinzufügen"
              className="w-5 h-5 grid place-items-center rounded text-gray-400 hover:text-[#2B2FB5] hover:bg-gray-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
              data-testid={`aufgaben-baum-plus-${k.id}`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          <StatusEtikett klasse={st.klasse}>{st.label}</StatusEtikett>
        </span>
        {(k.due_date || zaehler || (!kompakt && k.priority && k.priority !== 'medium')) && (
          <div className="col-start-3 col-span-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-gray-500 mt-0.5">
            {k.due_date && (
              <span className={ueberfaellig ? 'text-red-600 font-medium' : ''}>
                {ueberfaellig ? 'Überfällig · ' : ''}{deDatum(k.due_date)}{k.due_time ? `, ${k.due_time} Uhr` : ''}
              </span>
            )}
            {zaehler && (
              <span className="inline-flex items-center gap-1.5" title="Erledigte Unteraufgaben">
                <Fortschrittsbalken prozent={fortschritt(k)} breit={false} />
                {zaehler.erledigt}/{zaehler.gesamt}
              </span>
            )}
            {!kompakt && k.priority && k.priority !== 'medium' && (
              <span className="inline-flex items-center gap-1">
                <span className="w-[7px] h-[7px] rounded-full" style={{ background: prio.farbe }} />
                {prio.label}
              </span>
            )}
          </div>
        )}
      </div>
      {offen && (
        <div className="ml-[18px] pl-2.5 border-l border-dashed border-gray-200">
          {k.kinder.map((c) => <Zeile key={c.id} k={c} ebene={ebene + 1} zu={zu} umschalten={umschalten} props={props} />)}
        </div>
      )}
    </div>
  );
}

export default function AufgabenBaum(props) {
  const {
    aufgaben = [], erledigteGetrennt = true, speicherSchluessel = null, leerText = 'Keine offenen Aufgaben.',
    fusszeile = null, alleZuklappenSteuerung = null,
  } = props;
  const [zu, umschalten, alleSetzen] = useZugeklappt(speicherSchluessel);
  const [zeigeErledigt, setZeigeErledigt] = useState(false);

  const wurzeln = useMemo(() => baueBaum(aufgaben), [aufgaben]);
  const offene = erledigteGetrennt ? wurzeln.filter((k) => !istErledigt(k)) : wurzeln;
  const erledigte = erledigteGetrennt ? wurzeln.filter(istErledigt) : [];

  // Steuerung von außen: „Baum offen“ / „Nur Hauptaufgaben“
  if (alleZuklappenSteuerung) {
    alleZuklappenSteuerung.current = {
      alleAuf: () => alleSetzen([]),
      alleZu: () => alleSetzen(aufgaben.map((t) => t.id)),
    };
  }

  return (
    <div data-testid="aufgaben-baum">
      {offene.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">{leerText}</p>
      ) : (
        offene.map((k) => <Zeile key={k.id} k={k} ebene={0} zu={zu} umschalten={umschalten} props={props} />)
      )}
      {fusszeile}
      {erledigte.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setZeigeErledigt((z) => !z)}
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-gray-500 hover:text-gray-700 py-1"
            data-testid="aufgaben-baum-erledigte-toggle"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${zeigeErledigt ? '' : '-rotate-90'}`} />
            Abgeschlossen ({erledigte.length})
          </button>
          {zeigeErledigt && erledigte.map((k) => <Zeile key={k.id} k={k} ebene={0} zu={zu} umschalten={umschalten} props={props} />)}
        </div>
      )}
    </div>
  );
}
